import type { MicroAppsComponentSettings } from '@lumapps/micro-apps/types';

/**
 * Device permission types for iframe allow attribute
 */
export enum DevicePermissionType {
    CAMERA = 'camera',
    MICROPHONE = 'microphone',
    GEOLOCATION = 'geolocation',
    ACCELEROMETER = 'accelerometer',
    GYROSCOPE = 'gyroscope',
    MAGNETOMETER = 'magnetometer',
    DISPLAY_CAPTURE = 'display-capture',
    CLIPBOARD_WRITE = 'clipboard-write',
    CLIPBOARD_READ = 'clipboard-read',
    AMBIENT_LIGHT_SENSOR = 'ambient-light-sensor',
    AUTOPLAY = 'autoplay',
    BLUETOOTH = 'bluetooth',
    ENCRYPTED_MEDIA = 'encrypted-media',
    FULLSCREEN = 'fullscreen',
    GAMEPAD = 'gamepad',
    HID = 'hid',
    IDLE_DETECTION = 'idle-detection',
    LOCAL_FONTS = 'local-fonts',
    MIDI = 'midi',
    PAYMENT = 'payment',
    PICTURE_IN_PICTURE = 'picture-in-picture',
    PUBLICKEY_CREDENTIALS_CREATE = 'publickey-credentials-create',
    PUBLICKEY_CREDENTIALS_GET = 'publickey-credentials-get',
    SCREEN_WAKE_LOCK = 'screen-wake-lock',
    SERIAL = 'serial',
    SPEAKER_SELECTION = 'speaker-selection',
    STORAGE_ACCESS = 'storage-access',
    USB = 'usb',
    WEB_SHARE = 'web-share',
    WINDOW_MANAGEMENT = 'window-management',
    XR_SPATIAL_TRACKING = 'xr-spatial-tracking',
}

/**
 * Represents a single device permission entry
 */
export interface DevicePermission {
    /**
     * Unique identifier for React keys
     */
    id: string;
    /**
     * Type of device permission
     */
    type?: DevicePermissionType | null;
    /**
     * Permission value (free-form: '', '*', 'https://example.com', "'self' https://example.com")
     */
    value: string;
}

/**
 * Represents the settings for an Iframe component.
 * Extends MicroAppsInputVariables.
 */
export interface IframeSettings extends MicroAppsComponentSettings {
    /**
     * The height of the iframe.
     * @example '500px'
     */
    height?: string;
    /**
     * Allow iframe block to open in full screen mode by default
     */
    isDefaultFullScreen?: boolean;
    /**
     * The title of the iframe.
     */
    title?: string;
    /**
     * The URL of the content to be displayed in the iframe.
     */
    url: string;
    /**
     * The width of the iframe.
     * @example '100%'
     */
    width?: string;
    /**
     * Device hardware permissions for iframe allow attribute
     */
    devicePermissions?: DevicePermission[];
    /**
     * Sandbox permissions for iframe sandbox attribute
     */
    sandboxPermissions?: string[];
}

export type IframeMessageAction = 'close' | 'next';

export interface IframeBaseMessage {
    action: IframeMessageAction;
    data?: Record<string, unknown>;
}
